import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  REQUEST_TYPES,
  TYPE_HINT,
  TYPE_LABEL,
  useCreateRequest,
  requestableServices,
  useServices
} from "../../data/requestQueries";
import {
  attachmentRefusal,
  useComposeWithAttachments,
  type AttachmentUploadFailure
} from "../../data/attachmentQueries";
import { AttachmentComposer } from "../../components/AttachmentComposer";
import { draftKey, useSessionDraft } from "../../lib/drafts";
import { Sheet } from "../../ui/Sheet";
import { Callout } from "../../ui/Callout";

/**
 * Nytt ärende in a side sheet (`prototype/kund/pages.js` `SHEETS["nytt-arende"]`, approved
 * 2026-09-24). It was a centred dialog of its own until the second build round; the portal's one
 * `Sheet` is the browser's modal `<dialog>` too, so the page behind stays inert and Escape still
 * arrives as the dialog's `cancel`.
 *
 * The sheet's title is "Nytt ärende" and nothing stands under it. The dialog's old question heading
 * and its lead were a paragraph under a heading (design.md section 3, rule 3); the one sentence of it
 * that matters, no secrets here, is the line under Beskrivning.
 *
 * Mounted only while open, so the draft is read and the service list asked for only then.
 */
interface NewRequestDraft {
  serviceId: string;
  requestType: string;
  title: string;
  description: string;
}

/**
 * What the new-request draft holds, and never a file.
 *
 * Title and description are the text the draft exists for. The service and the type ride along
 * because restoring the words into a form whose two pickers had reset would hand back half a form;
 * both are ids and enum values, nothing a person wrote.
 */
const EMPTY_NEW_REQUEST: NewRequestDraft = {
  serviceId: "",
  requestType: "",
  title: "",
  description: ""
};

function isNewRequestDraft(value: unknown): value is NewRequestDraft {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (["serviceId", "requestType", "title", "description"] as const).every(
    (field) => typeof record[field] === "string"
  );
}

export function NewRequestSheet({
  tenantId,
  userId,
  onClose,
  onCreated
}: {
  tenantId: string;
  userId: string | null;
  /** Closed without a request. The list puts focus back on the button that opened the sheet. */
  onClose: () => void;
  /** A request exists; the list opens its page. */
  onCreated: (requestNumber: number) => void;
}) {
  const services = useServices(tenantId);
  const create = useCreateRequest();
  const compose = useComposeWithAttachments();
  const idempotencyKey = useRef(crypto.randomUUID());

  const [draft, setDraft, clearDraft] = useSessionDraft(
    draftKey(userId, tenantId, "new-request"),
    EMPTY_NEW_REQUEST,
    isNewRequestDraft
  );
  const { requestType, title, description } = draft;
  const update = (patch: Partial<NewRequestDraft>) => setDraft({ ...draft, ...patch });

  /**
   * The service the form sends, with one service chosen for the client. Added 2026-09-22.
   *
   * A client with exactly one service had to open a picker holding one option before the form would
   * send, which is a question with one answer. It is derived rather than written into state, so it
   * is not a draft and does not make the form count as typed in: an empty form with the one service
   * preselected still closes without asking. The picker stays on screen, so the choice is visible.
   *
   * A stored id that is no longer among the services, a draft written before a service was retired,
   * falls back the same way rather than leaving the select on a value it has no option for.
   */
  // Build round 46: an archived service is not offered, because the server refuses a request on it.
  const serviceList = requestableServices(services.data ?? []);
  const serviceId = serviceList.some((service) => service.id === draft.serviceId)
    ? draft.serviceId
    : serviceList.length === 1
      ? serviceList[0].id
      : "";

  const [files, setFiles] = useState<File[]>([]);
  const [sendFailures, setSendFailures] = useState<AttachmentUploadFailure[]>([]);
  /**
   * A request that exists and whose files did not all arrive.
   *
   * The sheet stays open to say so rather than closing on a success that is only half true. The
   * ärende is real and numbered, so the one thing to offer is the way to it.
   */
  const [createdNumber, setCreatedNumber] = useState<number | null>(null);

  const hasText = title.trim().length > 0 || description.trim().length > 0;

  /**
   * Ask before throwing away what somebody wrote. Added 2026-09-22.
   *
   * Avbryt, the X, Escape and a press on the backdrop all go through `requestClose`, and it asks
   * only when there is something to lose: typed text or a chosen file. A preselected service, or
   * pickers someone has set and nothing else, is not work, so an untouched form still closes on the
   * first press. The question is asked inside the sheet rather than through `window.confirm`, which
   * blocks the page and on a phone reads as the browser rather than the portal speaking.
   *
   * Once the request exists, with files that did not arrive, there is nothing to discard: the
   * ärende is real and the exit leads to it.
   */
  const dirty = hasText || files.length > 0;
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const keepWritingButton = useRef<HTMLButtonElement | null>(null);
  const focusBeforeConfirm = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (confirmingDiscard) {
      keepWritingButton.current?.focus();
    }
  }, [confirmingDiscard]);

  function requestClose() {
    if (createdNumber !== null) {
      onCreated(createdNumber);
      return;
    }
    if (dirty) {
      const active = document.activeElement;
      focusBeforeConfirm.current = active instanceof HTMLElement ? active : null;
      setConfirmingDiscard(true);
      return;
    }
    // Nothing typed, but a picker may have been set. That is not worth keeping either.
    clearDraft();
    onClose();
  }

  function keepWriting() {
    setConfirmingDiscard(false);
    const previous = focusBeforeConfirm.current;
    // Back where the person was, unless that control has gone; the title is the next best place.
    if (previous && previous.isConnected) {
      previous.focus();
    } else {
      document.getElementById("request-title-input")?.focus();
    }
  }

  function discard() {
    clearDraft();
    setConfirmingDiscard(false);
    onClose();
  }

  const refusedFile = files.some((file) => attachmentRefusal(file) !== null);
  const pending = create.isPending || compose.isPending;
  const failure = create.error ?? compose.error;
  const locked = pending || createdNumber !== null;
  const canSend =
    serviceId.length > 0 &&
    requestType.length > 0 &&
    (hasText || files.length > 0) &&
    !refusedFile &&
    !pending;

  /**
   * Text, a screenshot, or both.
   *
   * plan.md section 5.3's most common case is a fault with a screenshot. With a file the request
   * goes through `/api/files/compose`, and the gateway hangs the file from a first comment written by
   * the person who attached it. A request with neither text nor a file is still refused, by the
   * button here and by `PORTAL_EMPTY_REQUEST` on the server.
   */
  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!canSend) {
      return;
    }

    try {
      if (files.length === 0) {
        const created = await create.mutateAsync({
          tenantId,
          serviceId,
          title: title.trim(),
          description: description.trim(),
          requestType,
          idempotencyKey: idempotencyKey.current
        });
        idempotencyKey.current = crypto.randomUUID();
        clearDraft();
        onCreated(created.request_number);
        return;
      }

      const outcome = await compose.mutateAsync({
        tenantId,
        parent: {
          kind: "request",
          serviceId,
          title: title.trim() || null,
          description: description.trim() || null,
          requestType,
          idempotencyKey: idempotencyKey.current
        },
        files
      });
      idempotencyKey.current = crypto.randomUUID();
      // The request exists from here on, whatever happened to its files, so the draft has become the
      // request and keeping it would offer the same text a second time.
      clearDraft();

      if (outcome.requestNumber === null) {
        onClose();
        return;
      }

      if (outcome.failed.length === 0) {
        onCreated(outcome.requestNumber);
        return;
      }

      setFiles(outcome.failed.map((entry) => entry.file));
      setSendFailures(outcome.failed);
      setCreatedNumber(outcome.requestNumber);
    } catch {
      // Rendered below. The key is deliberately not rotated on failure, so a retry of the same
      // request cannot create a second one.
    }
  }

  return (
    <Sheet
      open
      title="Nytt ärende"
      testId="new-request-sheet"
      /*
        The X, Escape and the backdrop all arrive here. While the discard question shows, backing out
        answers it with "keep writing", because Escape means "back out of this" and the thing being
        backed out of is the question.
      */
      onOpenChange={(open) => {
        if (open) {
          return;
        }
        if (confirmingDiscard) {
          keepWriting();
          return;
        }
        requestClose();
      }}
    >
      <form className="k-new-request" onSubmit={onSubmit}>
        {createdNumber !== null ? (
          <Callout tone="warn" role="status" testId="create-partial">
            Ärendet REQ-{createdNumber} är skapat, men filerna nedan kom inte fram. Öppna ärendet och
            bifoga dem i en kommentar.
          </Callout>
        ) : null}

        <div className="box-field">
          <label htmlFor="request-service">Tjänst</label>
          <select
            id="request-service"
            required
            value={serviceId}
            disabled={locked}
            onChange={(event) => update({ serviceId: event.target.value })}
          >
            <option value="">Välj tjänst</option>
            {serviceList.map((service) => (
              <option key={service.id} value={service.id}>
                {service.display_name}
              </option>
            ))}
          </select>
        </div>

        {/*
          Radios rather than a select, because each type carries a line under it and a native option
          cannot (docs/polish.md P-16, 2026-09-24). The label wraps its radio, so the whole row is the
          target, and the fieldset announces the group as "Typ".
        */}
        <fieldset className="box-field k-type-choice" data-testid="request-type-choice">
          <legend>Typ</legend>
          {REQUEST_TYPES.map((type) => (
            <label key={type} className="k-type-option">
              <input
                type="radio"
                name="request-type"
                value={type}
                required
                checked={requestType === type}
                disabled={locked}
                onChange={() => update({ requestType: type })}
              />
              <span className="k-type-text">
                {/* The space keeps the accessible name two words apart; a grid drops it. */}
                <strong>{TYPE_LABEL[type]}</strong> <span>{TYPE_HINT[type]}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <div className="box-field">
          <label htmlFor="request-title-input">Rubrik</label>
          <input
            id="request-title-input"
            maxLength={200}
            value={title}
            disabled={locked}
            placeholder="Resultatet ni vill ha, i en mening"
            onChange={(event) => update({ title: event.target.value })}
          />
        </div>

        <div className="box-field">
          <label htmlFor="request-description">Beskrivning</label>
          <textarea
            id="request-description"
            rows={5}
            maxLength={20000}
            value={description}
            disabled={locked}
            aria-describedby="request-description-hint"
            onChange={(event) => update({ description: event.target.value })}
          />
          <p className="line-hint" id="request-description-hint">
            Inga lösenord här. Med en skärmdump får texten vara tom.
          </p>
        </div>

        <AttachmentComposer
          inputId="new-request-attachment"
          files={files}
          onChange={setFiles}
          disabled={locked}
          sendFailures={sendFailures}
        />

        {failure ? (
          <p className="form-error" role="alert" data-testid="create-error">
            {failure.message}
          </p>
        ) : null}

        <div className="k-sheet-actions">
          {createdNumber !== null ? (
            <button className="button is-primary" type="button" onClick={() => onCreated(createdNumber)}>
              Öppna ärendet
            </button>
          ) : confirmingDiscard ? (
            /*
              The question takes the place of the two actions rather than appearing beside them, so
              there is never a Skicka button next to a question about throwing the text away. Focus
              moves to "Fortsätt skriva", the answer that loses nothing, so an Enter pressed out of
              habit keeps the text.
            */
            <div
              className="k-discard"
              role="group"
              aria-labelledby="discard-confirm-question"
              data-testid="discard-confirm"
            >
              <p id="discard-confirm-question" role="alert">
                Vill ni kasta det ni har skrivit?
              </p>
              <div className="k-discard-actions">
                <button className="button" type="button" onClick={discard}>
                  Kasta texten
                </button>
                <button ref={keepWritingButton} className="button is-primary" type="button" onClick={keepWriting}>
                  Fortsätt skriva
                </button>
              </div>
            </div>
          ) : (
            <>
              <button className="button is-quiet" type="button" onClick={requestClose}>
                Avbryt
              </button>
              <button className="button is-primary" type="submit" disabled={!canSend}>
                {pending ? "Skickar" : "Skicka ärende"}
              </button>
            </>
          )}
        </div>
      </form>
    </Sheet>
  );
}
